import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { useProgressWithAutoRefresh } from "../../../shared/contexts/ProgressContext";
import { useStepRedirect, skipNextStepRedirect } from "../../../hooks/useStepRedirect";
import { RegistrationApiService } from "../api/RegistrationApiService";
import { OnboardingApiService } from "../../onboarding/api/OnboardingApiService";
import { normalizeAttachments } from "../../../shared/utils/attachmentUtils";
import { isOnboardingFlow } from "../utils/onboardingFlow";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";

interface ReviewScreenProps {
  onNext: () => void;
  onBack: () => void;
  onError: (error: string) => void;
}

interface ClinicData {
  id: string;
  name: string;
  governorate: string;
  area: { id: string; name: string; name_ar?: string } | null;
  neighborhood: string | number;
  street: string | number;
  house_number: string | number;
  nearest_landmark: string;
  latitude: number | null;
  longitude: number | null;
  clinic_work_hours: string;
  tax_number: string | null;
  applicant_notes: string | null;
}

const GOVERNORATE_DISPLAY: Record<string, string> = {
  baghdad: "Baghdad",
  basra: "Basrah",
  nineveh: "Nineveh",
  maysan: "Maysan",
  diyala: "Diyala",
  anbar: "Al Anbar",
  kirkuk: "Kirkuk",
  saladin: "Saladin",
  dohuk: "Duhok",
  erbil: "Erbil",
  sulaymaniyah: "Sulaymaniyah",
  halabja: "Halabja",
  najaf: "Najaf",
  karbala: "Karbala",
  babil: "Babylon",
  wasit: "Wasit",
  qadisiyah: "Al Qadisiyah",
  muthanna: "Al Muthanna",
  dhi_qar: "Dhiqar",
};

const ReviewScreen: React.FC<ReviewScreenProps> = ({ onNext, onBack, onError }) => {
  useStepRedirect();

  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const { progress } = useProgressWithAutoRefresh();

  const registrationApi = useMemo(() => new RegistrationApiService(), []);
  const onboardingApi = useMemo(() => new OnboardingApiService(), []);

  const [clinicData, setClinicData] = useState<ClinicData | null>(null);
  const [documents, setDocuments] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const hasLoadedData = useRef(false);

  const isOnboarding = isOnboardingFlow(progress);
  // The server submits an onboarding request the moment its last required document is uploaded
  // (on the documents step), so by the time this screen shows it is usually already submitted.
  // Say so instead of offering a «Submit» button that submits nothing.
  const onboardingRequest = isOnboarding
    ? progress?.active_requests?.find((r: any) => r.request_type === "onboarding")
    : null;
  // The request's own status (loaded below) wins over /progress, which can still say "draft" right
  // after the documents step.
  const [requestStatus, setRequestStatus] = useState<string | null>(null);
  const onboardingStatus = requestStatus || onboardingRequest?.status || null;
  const onboardingSubmitted = isOnboarding && !!onboardingStatus && onboardingStatus !== "draft";

  useEffect(() => {
    if (hasLoadedData.current || !progress) return;

    const loadData = async () => {
      hasLoadedData.current = true;
      setLoading(true);

      try {
        const requestType = isOnboarding ? 'onboarding' : 'registration';
        const activeRequest = progress?.active_requests?.find(
          (r: any) => r.request_type === requestType
        );
        if (!activeRequest) {
          setLoading(false);
          return;
        }

        const reqId = activeRequest.request_id;
        const detailResult = isOnboarding
          ? await onboardingApi.getOnboardingWithAttachments(reqId)
          : await registrationApi.getRegistrationWithAttachments(reqId);

        if (detailResult.isSuccess && detailResult.data) {
          const data = detailResult.data;
          setRequestStatus(data.status || null);
          setClinicData({
            id: data.id || reqId,
            name: data.name || "",
            governorate: data.governorate || "",
            area: data.area || null,
            neighborhood: data.neighborhood || "",
            street: data.street || "",
            house_number: data.house_number || "",
            nearest_landmark: data.nearest_landmark || "",
            latitude: data.latitude || null,
            longitude: data.longitude || null,
            clinic_work_hours: data.clinic_work_hours || "",
            tax_number: data.tax_number || null,
            applicant_notes: data.applicant_notes || null,
          });

          const rawDocs = data.attachments || data.documents;
          setDocuments(normalizeAttachments(rawDocs));
        }
      } catch (error) {
        console.error("[ReviewScreen] Failed to load data:", error);
        onError(error instanceof Error ? error.message : "Failed to load review data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOnboarding, registrationApi, onboardingApi, onError, progress]);

  const handleEditClinicInfo = () => {
    skipNextStepRedirect();
    navigate("/clinic_info_registration", { state: { fromReview: true } });
  };

  const handleEditPhotos = () => {
    skipNextStepRedirect();
    if (isOnboarding) {
      navigate("/onboarding_photo", { state: { fromReview: true } });
    } else {
      navigate("/clinic_photo", { state: { fromReview: true } });
    }
  };

  const handleContinue = () => {
    if (isOnboarding) {
      // Onboarding: no agree terms / payment, go straight to requests
      localStorage.removeItem("doctor_procedure");
      skipNextStepRedirect();
      navigate("/requests");
    } else {
      // Registration: proceed to agree terms
      skipNextStepRedirect();
      navigate("/agree_terms");
    }
  };

  const handleBack = () => {
    skipNextStepRedirect();
    if (isOnboarding) {
      navigate("/onboarding_photo");
    } else {
      navigate("/clinic_photo");
    }
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  if (!clinicData) {
    return (
      <ResponsiveFormLayout
        title={t("review_application")}
        subtitle={t("error")}
        showLanguageToggle
        maxHeight="90vh"
      >
        <p className="text-center text-gray-600">{t("loading_failed")}</p>
      </ResponsiveFormLayout>
    );
  }

  // GOVERNORATE_DISPLAY maps the API enum to the English name, which is also the
  // translation key — without t() this renders "Baghdad" even in Arabic.
  const displayGovernorate = t(
    GOVERNORATE_DISPLAY[clinicData.governorate] || clinicData.governorate
  );

  const formatDocKey = (k: string) => {
    const translated = t(k);
    if (translated !== k) return translated;
    const titleKey = `${k}_title`;
    const titleTranslated = t(titleKey);
    if (titleTranslated !== titleKey) return titleTranslated;
    return k.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const getDocSortOrder = (k: string) => {
    if (k.includes("signboard")) return 1;
    if (k.includes("prescription")) return 2;
    if (k.includes("rooms")) return 3;
    return 4;
  };

  const InfoRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => {
    if (value === null || value === undefined || value === "") return null;
    return (
      <div className="flex justify-between items-start py-2 border-b border-gray-100">
        <span className="text-sm text-gray-500">{label}</span>
        <span className="text-sm text-gray-900 font-medium">{value}</span>
      </div>
    );
  };

  const SectionHeader = ({ title, onEdit }: { title: string; onEdit: () => void }) => (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-base font-bold text-gray-800">{title}</h3>
      {/* A submitted request can no longer be edited by the doctor. */}
      {!onboardingSubmitted && (
        <button
          onClick={onEdit}
          className="text-sm font-semibold text-[#2E5D7D] hover:underline"
        >
          {t("edit")}
        </button>
      )}
    </div>
  );

  const continueButton = (
    <div className="flex gap-3 w-full">
      <button
        onClick={handleBack}
        className="min-h-[56px] px-6 py-4 rounded-2xl text-base font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200"
      >
        {t("back") || "Back"}
      </button>
      <button
        onClick={handleContinue}
        className="flex-1 min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 shadow-md hover:shadow-lg hover:opacity-90"
        style={{ backgroundColor: "#2E5D7D" }}
      >
        {onboardingSubmitted ? t("view_my_requests") : isOnboarding ? t("submit_application") : t("continue")}
      </button>
    </div>
  );

  return (
    <>
    <ResponsiveFormLayout
      onBack={handleBack}
      showBackButton={true}
      showLanguageToggle
      icon="/ima_logo.png"
      iconSize="w-32 h-32"
      title={t("review_application")}
      subtitle={onboardingSubmitted ? t("onboarding_already_submitted") : t("review_subtitle")}
      actions={continueButton}
      progressValue={isOnboarding ? 90 : 75}
      maxHeight="80vh"
    >
      <div className="w-full space-y-5">
        {/* Clinic Information Section */}
        <div className="p-4 rounded-xl border border-gray-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <SectionHeader title={t("clinic_details")} onEdit={handleEditClinicInfo} />
          <div className="bg-white p-4 rounded-lg space-y-0">
            <InfoRow label={t("clinic_name")} value={clinicData.name} />
            <InfoRow label={t("governorate")} value={displayGovernorate} />
            <InfoRow label={t("city")} value={isRTL ? clinicData.area?.name_ar : clinicData.area?.name} />
            <InfoRow label={t("neighborhood")} value={clinicData.neighborhood} />
            <InfoRow label={t("street")} value={clinicData.street} />
            <InfoRow label={t("house_number")} value={clinicData.house_number} />
            <InfoRow label={t("nearest_landmark")} value={clinicData.nearest_landmark} />
            <InfoRow
              label={t("working_hours")}
              value={clinicData.clinic_work_hours === "morning" ? t("morning") : t("evening")}
            />
            {/* Tax number is no longer collected in this flow, so it is not shown here. */}
            <InfoRow label={t("applicant_notes")} value={clinicData.applicant_notes} />
          </div>
        </div>

        {/* Documents Section */}
        <div className="p-4 rounded-xl border border-gray-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <SectionHeader title={t("uploaded_documents")} onEdit={handleEditPhotos} />
          {Object.keys(documents).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(documents)
                .sort(([a], [b]) => getDocSortOrder(a) - getDocSortOrder(b))
                .map(([key, value]) => {
                  if (!value) return null;

                  if (Array.isArray(value)) {
                    return (
                      <div key={key} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-sm font-semibold text-gray-900">
                            {formatDocKey(key)} ({value.length})
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {value.map((url: string, index: number) => (
                            <div
                              key={index}
                              onClick={() => setSelectedImage(url)}
                              className="relative cursor-pointer group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#2E5D7D] transition-all"
                            >
                              <img
                                src={url}
                                alt={`${formatDocKey(key)} ${index + 1}`}
                                className="w-full h-32 object-cover"
                                loading="lazy"
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                <span className="text-white text-xs font-medium">
                                  {formatDocKey(key)} {index + 1}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={key} className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-gray-900">{formatDocKey(key)}</span>
                      </div>
                      <div
                        onClick={() => setSelectedImage(value)}
                        className="relative cursor-pointer group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#2E5D7D] transition-all"
                      >
                        <img
                          src={value}
                          alt={formatDocKey(key)}
                          className="w-full h-40 object-cover"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="bg-white p-4 rounded-lg">
              <p className={`text-sm text-gray-400 ${isRTL ? "text-right" : "text-left"}`}>
                {t("not_uploaded")}
              </p>
            </div>
          )}
        </div>
      </div>
    </ResponsiveFormLayout>

    {/* Image Lightbox */}
    {selectedImage && (
      <div
        className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center p-4"
        style={{ zIndex: 9999999 }}
        onClick={() => setSelectedImage(null)}
      >
        <button
          onClick={() => setSelectedImage(null)}
          className="absolute top-4 left-4 text-white text-4xl hover:text-gray-300 transition-all z-10 flex items-center gap-2"
        >
          <span>←</span>
          <span className="text-lg font-semibold">{t("close")}</span>
        </button>
        <img
          src={selectedImage}
          alt={t("document_preview")}
          className="max-w-full max-h-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
    </>
  );
};

export default ReviewScreen;
